import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, Copy, Download, ExternalLink, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { api, ApiError, formatDate, formatDateTime } from "../../api";
import { useApi } from "../../hooks";
import { useAuth } from "../../auth";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../Ui";
import ContactDrawer from "./ContactDrawer";
import QuickMenu, { QuickMenuItem } from "../QuickMenu";
import { contactName } from "../../crm";
import { attributionRows, cameFrom, embedCode } from "../../crmAttribution";
import { confirmLeave, setLeaveGuard } from "../../leave-guard";
import type { Calendar, CrmForm, FormOption, FormQuestion, FormSettings, Person, QuestionType } from "./types";
import type { PreviewScreen } from "../../pages/PublicForm";
import FormPreview from "./FormPreview";

const TYPE_LABEL: Record<QuestionType, string> = {
  choice: "Pick one",
  multi: "Pick any",
  text: "Short answer",
  long_text: "Long answer",
  contact: "Contact details",
};
const TABS = [
  { id: "questions", label: "Questions" },
  { id: "finish", label: "After submit" },
  { id: "share", label: "Share" },
  { id: "leads", label: "Leads" },
] as const;
type Tab = (typeof TABS)[number]["id"];

const rid = (p: string) => `${p}_${Math.random().toString(36).slice(2, 8)}`;
const blank = (type: QuestionType): FormQuestion =>
  type === "choice" || type === "multi"
    ? { id: rid("q"), type, label: "", required: true, options: [{ id: rid("o"), label: "" }, { id: rid("o"), label: "" }] }
    : { id: rid("q"), type, label: "", required: false };

type Submission = {
  id: number; created_at: string; qualified: boolean; booked: boolean; contact_id: number | null;
  first_name: string; last_name: string; email: string; phone: string; company: string;
  dq_reasons: string[]; answers: { question: string; answer: string }[]; utm: Record<string, string>;
};
/** GET /api/crm/forms/:id/stats (utils/crmFormStats.js). */
type FormStats = {
  days: number; since: string | null; since_tracking: boolean;
  views: number; started: number; finished: number; qualified: number; booked: number; completion: number | null;
  /** Typed their email and left without sending it. */
  left?: number;
  steps: { id: string; label: string; reached: number; stopped: number }[];
};
const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
const count = (n: number) => n.toLocaleString("en-US");
const percent = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—");

/** A live landing page (Pages) that shows this form. */
type LivePage = { id: string; name: string; slug: string };

/**
 * The live landing pages a change would take this form off: the server's 409 form_in_use
 * (routes/crm.js), or null for any other error.
 */
function livePagesOf(e: unknown): LivePage[] | null {
  if (!(e instanceof ApiError) || e.status !== 409 || e.data?.code !== "form_in_use") return null;
  const pages = Array.isArray(e.data.pages) ? e.data.pages as Partial<LivePage>[] : [];
  return pages.filter((p): p is LivePage => typeof p?.name === "string").map((p) => ({ id: String(p.id), name: p.name, slug: String(p.slug) }));
}

/** The second question before a change takes the form off live pages, naming them (five at most). */
function livePagesQuestion(formName: string, pages: LivePage[], doing: "delete" | "turn off"): string {
  const names = pages.slice(0, 5).map((p) => `"${p.name}"`);
  const more = pages.length - names.length;
  const list = more > 0 ? `${names.join(", ")} and ${more} more` : names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "";
  const one = pages.length === 1;
  const where = one ? `the live page ${list}` : `${pages.length} live pages: ${list}`;
  const them = one ? "that page" : "those pages";
  return doing === "delete"
    ? `"${formName}" is on ${where}. Deleting it removes the form from ${them} for good. Delete anyway?`
    : `"${formName}" is on ${where}. Turning it off removes the form from ${them} until you go live again. Turn it off anyway?`;
}

/** One lead form: its questions, what happens after submit, how to share it, and its leads. */
export default function FormBuilder({ id, onBack }: { id: number; onBack: () => void }) {
  const loaded = useApi<CrmForm>(`/api/crm/forms/${id}`);
  const calendars = useApi<Calendar[]>("/api/crm/calendars");
  const people = useApi<Person[]>("/api/tasks/assignees");
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "exec";
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.id === params.get("tab"))?.id || "questions") as Tab;
  const setTab = (t: Tab) => setParams((cur) => { const n = new URLSearchParams(cur); n.set("tab", t); return n; });
  const [draft, setDraft] = useState<CrmForm | null>(null);
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  // The screen the live preview shows: whatever part of the form is being edited.
  const [screen, setScreen] = useState<PreviewScreen>(() => (tab === "finish" ? { kind: "end", qualified: true } : { kind: "intro" }));
  useEffect(() => {
    if (tab === "questions") setScreen({ kind: "intro" });
    if (tab === "finish") setScreen({ kind: "end", qualified: true });
  }, [tab]);

  useEffect(() => {
    if (loaded.data && !draft) { setDraft(loaded.data); setSaved(JSON.stringify(loaded.data)); }
  }, [loaded.data, draft]);
  const dirty = draft ? JSON.stringify(draft) !== saved : false;
  useEffect(() => {
    if (!dirty) return;
    // In-app links (sidebar, tabs) ask first; closing the tab asks through the browser.
    setLeaveGuard("This form has changes that are not saved. Leave without saving?");
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => { setLeaveGuard(null); window.removeEventListener("beforeunload", warn); };
  }, [dirty]);

  if (loaded.loading && !draft) return <LoadingState label="Loading form" />;
  if (loaded.error || !draft) return <ErrorState message={loaded.error || "This form could not load."} onRetry={loaded.reload} />;

  const f = draft;
  const set = (patch: Partial<CrmForm>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setS = (patch: Partial<FormSettings>) => setDraft((d) => (d ? { ...d, settings: { ...d.settings, ...patch } } : d));
  const setQ = (i: number, patch: Partial<FormQuestion>) => set({ questions: f.questions.map((q, k) => (k === i ? { ...q, ...patch } : q)) });

  async function remove() {
    if (!window.confirm(`Delete "${f.name}"? Its link stops working. Leads it already sent stay in the CRM.`)) return;
    setBusy(true);
    try {
      try {
        await api(`/api/crm/forms/${id}`, { method: "DELETE" });
      } catch (e) {
        // The form is on live landing pages: name them, and delete only on a second yes.
        const pages = livePagesOf(e);
        if (!pages) throw e;
        if (!window.confirm(livePagesQuestion(f.name, pages, "delete"))) { setBusy(false); return; }
        await api(`/api/crm/forms/${id}?confirm=1`, { method: "DELETE" });
      }
      setLeaveGuard(null); setSaved(JSON.stringify(f)); onBack();
    } catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "The form was not deleted." }); setBusy(false); }
  }

  async function save(status?: "draft" | "live") {
    setBusy(true); setNotice(null);
    const put = (confirm: boolean) => api<CrmForm>(`/api/crm/forms/${id}`, {
      method: "PUT",
      body: JSON.stringify({ name: f.name, slug: f.slug, status: status || f.status, questions: f.questions, settings: f.settings, ...(confirm ? { confirm: true } : {}) }),
    });
    try {
      let out: CrmForm;
      try {
        out = await put(false);
      } catch (e) {
        // Turning off a form that live landing pages show: name them, and go on only on a second yes.
        const pages = livePagesOf(e);
        if (!pages) throw e;
        if (!window.confirm(livePagesQuestion(f.name, pages, "turn off"))) return;
        out = await put(true);
      }
      setDraft(out); setSaved(JSON.stringify(out));
      setNotice({ ok: true, text: status === "live" ? "Saved. The form is live." : status === "draft" ? "Saved. The form is back to draft, so its link is off." : "Saved." });
    } catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "The form did not save." }); }
    finally { setBusy(false); }
  }

  const origin = window.location.origin;
  const link = `${origin}/f/${f.slug}`;
  const savedForm = JSON.parse(saved) as CrmForm;
  /** A live form whose link name changed: the old link (in ads, emails, embeds) stops working on save. */
  const slugChanged = savedForm.status === "live" && f.slug !== savedForm.slug;
  const turnOff = () => {
    if (window.confirm(`Turn off "${f.name}"? Its link and embeds show "not available" until you go live again.`)) void save("draft");
  };

  return (
    <>
      <button type="button" className="text-button crm-back" onClick={() => { if (confirmLeave()) onBack(); }}><ArrowLeft aria-hidden="true" />All forms</button>
      <PageHeader
        title={f.name || "Untitled form"}
        actions={
          <div className="crm-actions">
            <span className={`crm-status ${f.status}`}>{f.status === "live" ? "Live" : "Draft"}</span>
            <a className="button secondary" href={`/f/${f.slug}?preview=1`} target="_blank" rel="noreferrer"><ExternalLink aria-hidden="true" />Preview</a>
            {canWrite && (f.status === "live"
              ? <button className="button secondary" disabled={busy} onClick={turnOff}>Turn off</button>
              : <button className="button secondary" disabled={busy} onClick={() => void save("live")}>Go live</button>)}
            {/* While the save bar shows (every tab but Leads), Save lives there only. */}
            {canWrite && (dirty
              ? tab === "leads" && <button className="button" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
              : <span className="crm-saved" aria-live="polite">All changes saved</span>)}
            {canWrite && (
              <QuickMenu label="More form actions" align="right" trigger={<span className="icon-button crm-more-trigger" title="More"><MoreHorizontal aria-hidden="true" /></span>}>
                {(close) => <QuickMenuItem tone="danger" onClick={() => { close(); void remove(); }}>Delete form</QuickMenuItem>}
              </QuickMenu>
            )}
          </div>
        }
      />
      {notice && <p className={`inline-notice${notice.ok ? "" : " danger"}`} role={notice.ok ? "status" : "alert"}>{notice.text}</p>}
      <div className="tabs crm-tabs" role="tablist" aria-label="Form sections">
        {TABS.map((t) => <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>

      {(tab === "questions" || tab === "finish") && (
      <div className="crm-fb-split">
      {tab === "questions" && (
        <div className="crm-builder">
          <section className="panel crm-fb-card" onFocusCapture={() => setScreen({ kind: "intro" })}>
            <h3>Form</h3>
            <div className="crm-edit-grid">
              <label className="wide">Name (only your team sees it)<input value={f.name} disabled={!canWrite} onChange={(e) => set({ name: e.target.value })} maxLength={120} /></label>
              <label className="wide"><span>Welcome title <span className="crm-muted">Optional. Leave empty to start on the first question.</span></span><input value={f.settings.intro_title} disabled={!canWrite} onChange={(e) => setS({ intro_title: e.target.value })} maxLength={160} /></label>
              {f.settings.intro_title && <label className="wide">Welcome text<textarea rows={2} value={f.settings.intro_body} disabled={!canWrite} onChange={(e) => setS({ intro_body: e.target.value })} maxLength={600} /></label>}
              {f.settings.intro_title && <label>Start button<input value={f.settings.start_label} disabled={!canWrite} onChange={(e) => setS({ start_label: e.target.value })} maxLength={40} /></label>}
              <label>Button color
                <span className="color-field">
                  <input type="color" aria-label="Button color swatch" value={f.settings.accent} disabled={!canWrite} onChange={(e) => setS({ accent: e.target.value })} />
                  <input value={f.settings.accent} disabled={!canWrite} onChange={(e) => setS({ accent: e.target.value.trim() })} maxLength={7} spellCheck={false} />
                </span>
              </label>
            </div>
          </section>
          <ol className="crm-fb-list">
            {f.questions.map((q, i) => (
              <li key={q.id} className="panel crm-fb-card" onFocusCapture={() => setScreen({ kind: "question", id: q.id })}>
                <div className="crm-fb-head">
                  <span className="crm-fb-num">{i + 1}</span>
                  <span className="crm-fb-type">{TYPE_LABEL[q.type]}</span>
                  {canWrite && (
                    <span className="crm-fb-tools">
                      <button type="button" className="icon-button" aria-label="Move up" disabled={i === 0} onClick={() => { const qs = [...f.questions]; [qs[i - 1], qs[i]] = [qs[i], qs[i - 1]]; set({ questions: qs }); }}><ArrowUp aria-hidden="true" /></button>
                      <button type="button" className="icon-button" aria-label="Move down" disabled={i === f.questions.length - 1} onClick={() => { const qs = [...f.questions]; [qs[i + 1], qs[i]] = [qs[i], qs[i + 1]]; set({ questions: qs }); }}><ArrowDown aria-hidden="true" /></button>
                      {q.type !== "contact" && <button type="button" className="icon-button" aria-label="Delete question" onClick={() => set({ questions: f.questions.filter((_, k) => k !== i) })}><Trash2 aria-hidden="true" /></button>}
                    </span>
                  )}
                </div>
                <label>Question<input value={q.label} disabled={!canWrite} placeholder="Ask one thing" onChange={(e) => setQ(i, { label: e.target.value })} maxLength={300} /></label>
                <label><span>Help text <span className="crm-muted">Optional</span></span><input value={q.help || ""} disabled={!canWrite} onChange={(e) => setQ(i, { help: e.target.value })} maxLength={500} /></label>
                {(q.type === "choice" || q.type === "multi") && (
                  <OptionsEditor options={q.options || []} canWrite={canWrite} onChange={(options) => setQ(i, { options })} />
                )}
                {q.type === "contact" ? (
                  <div className="crm-fb-fields">
                    <span className="crm-muted">Always asks first name, last name and email.</span>
                    <label className="crm-check"><input type="checkbox" disabled={!canWrite} checked={q.fields?.phone !== false} onChange={(e) => setQ(i, { fields: { ...q.fields, phone: e.target.checked } })} />Phone (required)</label>
                    <label className="crm-check"><input type="checkbox" disabled={!canWrite} checked={Boolean(q.fields?.company)} onChange={(e) => setQ(i, { fields: { ...q.fields, company: e.target.checked } })} />Company</label>
                    {q.fields?.company && <label>Company field label<input value={q.fields.company_label || ""} disabled={!canWrite} placeholder="Company" onChange={(e) => setQ(i, { fields: { ...q.fields, company_label: e.target.value } })} maxLength={60} /></label>}
                    <label className="crm-check"><input type="checkbox" disabled={!canWrite} checked={Boolean(q.fields?.title)} onChange={(e) => setQ(i, { fields: { ...q.fields, title: e.target.checked } })} />Job title</label>
                  </div>
                ) : (
                  <label className="crm-check"><input type="checkbox" disabled={!canWrite} checked={q.required} onChange={(e) => setQ(i, { required: e.target.checked })} />Required</label>
                )}
              </li>
            ))}
          </ol>
          {canWrite && (
            <div className="crm-fb-add">
              <span>Add a question</span>
              {(["choice", "multi", "text", "long_text"] as QuestionType[]).map((t) => (
                <button key={t} type="button" className="button secondary small" onClick={() => {
                  const at = f.questions.findIndex((q) => q.type === "contact");
                  const qs = [...f.questions];
                  const added = blank(t);
                  // New questions go before the contact screen, which stays last.
                  qs.splice(at === -1 ? qs.length : at, 0, added);
                  set({ questions: qs });
                  setScreen({ kind: "question", id: added.id });
                }}><Plus aria-hidden="true" />{TYPE_LABEL[t]}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "finish" && (
        <div className="crm-builder">
          <section className="panel crm-fb-card" onFocusCapture={() => setScreen({ kind: "end", qualified: true })}>
            <h3>Qualified leads</h3>
            <div className="crm-edit-grid">
              <label className="wide">Title<input value={f.settings.thanks_title} disabled={!canWrite} onChange={(e) => setS({ thanks_title: e.target.value })} maxLength={160} /></label>
              <label className="wide">Text<textarea rows={2} value={f.settings.thanks_body} disabled={!canWrite} onChange={(e) => setS({ thanks_body: e.target.value })} maxLength={600} /></label>
              <label>Book a call on
                <select value={f.settings.calendar_id ?? ""} disabled={!canWrite} onChange={(e) => setS({ calendar_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">No calendar</option>
                  {(calendars.data || []).filter((c) => c.active || c.id === f.settings.calendar_id).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.duration_min} min)</option>)}
                </select>
              </label>
              {(calendars.data || []).some((c) => c.id === f.settings.calendar_id && !c.active) && (
                <p className="inline-notice danger wide" role="alert">This calendar is turned off, so qualified leads see this text but no times to pick. Turn it on in <Link to="/crm/bookings">Bookings</Link>, or pick another calendar.</p>
              )}
              {!f.settings.calendar_id && <label><span>Then send them to <span className="crm-muted">Optional</span></span><input type="url" value={f.settings.redirect_url} disabled={!canWrite} placeholder="https://" onChange={(e) => setS({ redirect_url: e.target.value })} maxLength={500} /></label>}
            </div>
            {!(calendars.data || []).length && <p className="crm-muted">Add a calendar on the Bookings tab to let leads book right after the form.</p>}
          </section>
          <section className="panel crm-fb-card" onFocusCapture={() => setScreen({ kind: "end", qualified: false })}>
            <h3>Not a fit</h3>
            <p className="crm-muted">Shown when an answer marked "Not a fit" was picked. These leads are still saved, under Not a fit.</p>
            <div className="crm-edit-grid">
              <label className="wide">Title<input value={f.settings.dq_title} disabled={!canWrite} onChange={(e) => setS({ dq_title: e.target.value })} maxLength={160} /></label>
              <label className="wide">Text<textarea rows={2} value={f.settings.dq_body} disabled={!canWrite} onChange={(e) => setS({ dq_body: e.target.value })} maxLength={600} /></label>
            </div>
          </section>
          <section className="panel crm-fb-card">
            <h3>Lead handling</h3>
            <div className="crm-edit-grid">
              <label>Lead owner
                <select value={f.settings.owner_id ?? ""} disabled={!canWrite} onChange={(e) => setS({ owner_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">Unassigned</option>
                  {(people.data || []).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                </select>
              </label>
            </div>
          </section>
        </div>
      )}
        <FormPreview form={f} savedSlug={savedForm.slug} screen={screen} />
      </div>
      )}

      {tab === "share" && (
        <div className="crm-builder">
          {f.status !== "live" && <p className="inline-notice">This form is a draft. Press Go live before you share it; until then the link shows "not available" to everyone but your team.</p>}
          <section className="panel crm-fb-card">
            <h3>Link</h3>
            <p className="crm-muted">A full-page form. Use it as the landing page for ads, emails and texts.</p>
            <CopyBox value={link} />
            <label>Link name<span className="crm-slug"><span>/f/</span><input value={f.slug} disabled={!canWrite} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").slice(0, 60) })} /></span></label>
            {slugChanged && <p className="crm-panel-note" role="status">This form is live. Once you save, the old link /f/{savedForm.slug} stops working, so update any ads, emails or embeds that use it.</p>}
          </section>
          <section className="panel crm-fb-card">
            <h3>Embed on a website</h3>
            <p className="crm-muted">Paste this where the form should appear. It fits its height to the form and sends the visit's ad tags (UTMs, gclid, gbraid, wbraid and more) with each lead. It keeps them for 90 days, so a visitor who clicked an ad on another page still counts when that page has this form or the NOLA tracking script.</p>
            <CopyBox value={embedCode(link, f.slug, f.name)} multiline />
          </section>
        </div>
      )}

      {tab === "leads" && <Leads formId={id} />}
      {canWrite && dirty && tab !== "leads" && (
        // The question list runs long; Save follows you down it.
        <div className="crm-savebar" role="region" aria-label="Unsaved changes">
          <span>Unsaved changes</span>
          <button type="button" className="button secondary small" disabled={busy} onClick={() => { if (loaded.data) { setDraft(JSON.parse(saved)); setNotice(null); } }}>Discard</button>
          <button type="button" className="button small" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
        </div>
      )}
    </>
  );
}

function OptionsEditor({ options, canWrite, onChange }: { options: FormOption[]; canWrite: boolean; onChange: (o: FormOption[]) => void }) {
  return (
    <div className="crm-fb-options">
      <span className="crm-fb-label">Answers</span>
      {options.map((o, i) => (
        <div key={o.id} className="crm-fb-option">
          <input aria-label={`Answer ${i + 1}`} value={o.label} disabled={!canWrite} placeholder={`Answer ${i + 1}`} onChange={(e) => onChange(options.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} maxLength={200} />
          <label className="crm-check" title="Picking this answer marks the lead as not a fit"><input type="checkbox" disabled={!canWrite} checked={Boolean(o.disqualify)} onChange={(e) => onChange(options.map((x, k) => (k === i ? { ...x, disqualify: e.target.checked } : x)))} />Not a fit</label>
          {canWrite && <button type="button" className="icon-button" aria-label={`Remove answer ${i + 1}`} disabled={options.length <= 2} onClick={() => onChange(options.filter((_, k) => k !== i))}><Trash2 aria-hidden="true" /></button>}
        </div>
      ))}
      {canWrite && options.length < 20 && <button type="button" className="text-button" onClick={() => onChange([...options, { id: rid("o"), label: "" }])}><Plus aria-hidden="true" />Add answer</button>}
    </div>
  );
}

function CopyBox({ value, multiline = false }: { value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(value); setCopied(true); window.setTimeout(() => setCopied(false), 1800); } catch { /* the text is selectable */ }
  }
  return (
    <div className={`crm-copy${multiline ? " multi" : ""}`}>
      {multiline ? <textarea readOnly rows={5} value={value} onFocus={(e) => e.currentTarget.select()} aria-label="Embed code" /> : <input readOnly value={value} onFocus={(e) => e.currentTarget.select()} aria-label="Form link" />}
      <button type="button" className="button secondary small" onClick={() => void copy()}><Copy aria-hidden="true" />{copied ? "Copied" : "Copy"}</button>
    </div>
  );
}

function Leads({ formId }: { formId: number }) {
  const subs = useApi<Submission[]>(`/api/crm/forms/${formId}/submissions`);
  const stats = useApi<FormStats>(`/api/crm/forms/${formId}/stats?days=30`);
  const [params, setParams] = useSearchParams();
  const contactId = Number(params.get("contact")) || null;
  const open = (id: number | null) => setParams((cur) => { const n = new URLSearchParams(cur); if (id) n.set("contact", String(id)); else n.delete("contact"); return n; });
  const rows = subs.data || [];
  const qualified = useMemo(() => rows.filter((r) => r.qualified).length, [rows]);
  const reload = () => { subs.reload(); stats.reload(); };
  return (
    <>
      {stats.data && <FormNumbers stats={stats.data} />}
      {subs.loading && !subs.data ? <LoadingState label="Loading leads" />
        : subs.error ? <ErrorState message={subs.error} onRetry={subs.reload} />
        : !rows.length ? <section className="panel"><EmptyState title="No leads yet" description="Every answer to this form lands here and in your pipeline." /></section>
        : (
          <section className="panel table-wrap">
            {/* The numbers above cover their own dates ("Since Oct 9"); this list is every lead the form ever sent. */}
            <div className="crm-panel-head">
              <h2>Leads</h2>
              <span className="crm-muted crm-panel-period">All time</span>
              <div className="toolbar-end">
                <a className="button secondary small" href={`${API_BASE}/api/crm/forms/${formId}/submissions.csv`} download><Download aria-hidden="true" />Export CSV</a>
              </div>
            </div>
            <table className="module-table crm-table crm-leads">
              <thead><tr><th>Name</th><th>Company</th><th>Came from</th><th>Result</th><th>Booked</th><th>Answers</th><th>When</th></tr></thead>
              <tbody>
                {rows.map((s) => {
                  const from = cameFrom(s.utm);
                  return (
                    <tr key={s.id} className={s.contact_id ? "is-clickable" : undefined} onClick={() => s.contact_id && open(s.contact_id)}>
                      <td data-label="Name">{s.contact_id ? <button type="button" className="crm-row-open" onClick={(e) => { e.stopPropagation(); open(s.contact_id); }}>{contactName(s)}</button> : contactName(s)}</td>
                      <td data-label="Company" data-empty={s.company ? undefined : ""}>{s.company || <span className="crm-muted">—</span>}</td>
                      <td data-label="Came from" data-empty={from ? undefined : ""} className="crm-came-from">
                        {from ? <span title={attributionRows(s.utm).map((r) => `${r.label}: ${r.value}`).join("\n")}>{from}</span> : <span className="crm-muted">—</span>}
                      </td>
                      <td data-label="Result">{s.qualified ? <span className="crm-stage s-qualified">Qualified</span> : <span className="crm-stage s-dq" title={s.dq_reasons.join("; ")}>Not a fit</span>}</td>
                      <td data-label="Booked">{s.booked ? "Yes" : <span className="crm-muted">No</span>}</td>
                      <td data-label="Answers" className="crm-answers-cell">
                        <dl className="crm-answer-lines">{s.answers.map((a, i) => <div key={i}><dt>{a.question}</dt><dd>{a.answer}</dd></div>)}</dl>
                      </td>
                      <td data-label="When" className="crm-muted">{formatDateTime(s.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="crm-table-foot"><span>{rows.length} {rows.length === 1 ? "lead" : "leads"}, {qualified} qualified</span></div>
          </section>
        )}
      {contactId && <ContactDrawer key={contactId} id={contactId} onClose={() => open(null)} onChanged={reload} />}
    </>
  );
}

/**
 * The form's numbers, Typeform style: who opened it, started, finished and booked, and
 * the question each of the rest stopped on. Counted from when the form page started
 * reporting, so "Since <day>" shows until that is 30 days back.
 */
function FormNumbers({ stats }: { stats: FormStats }) {
  const period = stats.since_tracking && stats.since ? `Since ${formatDate(stats.since)}` : `Last ${stats.days} days`;
  const steps = stats.steps.some((s) => s.reached > 0) ? stats.steps : [];
  const worst = steps.reduce((at, s, i) => (s.stopped > (steps[at]?.stopped ?? 0) ? i : at), -1);
  const intro = steps[0]?.id === "_intro" ? 1 : 0;
  return (
    <div className="crm-fnums">
      <p className="crm-fnums-period">{period}</p>
      <div className="crm-stats" role="group" aria-label={`Form numbers, ${period.toLowerCase()}`}>
        <div><span>Views</span><strong>{count(stats.views)}</strong></div>
        <div><span>Started</span><strong>{count(stats.started)}</strong>{(stats.left ?? 0) > 0 && <small>{count(stats.left ?? 0)} left their email</small>}</div>
        <div><span>Finished</span><strong>{count(stats.finished)}</strong>{stats.finished > 0 && <small>{count(stats.qualified)} qualified</small>}</div>
        <div><span>Completion</span><strong>{stats.completion === null ? "—" : `${Math.round(stats.completion * 100)}%`}</strong></div>
        <div><span>Booked</span><strong>{count(stats.booked)}</strong></div>
      </div>
      {steps.length > 0 && (
        <section className="panel table-wrap crm-fnums-steps">
          <div className="crm-panel-head"><h2>Where people stop</h2></div>
          <table className="module-table crm-table crm-funnel">
            <thead><tr><th>Question</th><th className="num">Reached</th><th className="num">Stopped here</th><th className="num">Drop-off</th></tr></thead>
            <tbody>
              {steps.map((s, i) => (
                <tr key={s.id} className={i === worst ? "is-worst" : undefined}>
                  <td data-label="Question">{s.id === "_intro" ? s.label : <><span className="crm-funnel-num">{i + 1 - intro}</span>{s.label}</>}</td>
                  <td data-label="Reached" className="num">{count(s.reached)}</td>
                  <td data-label="Stopped here" className="num">{count(s.stopped)}</td>
                  <td data-label="Drop-off" className="num">{percent(s.stopped, s.reached)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
